import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  where,
  updateDoc
} from "firebase/firestore";
import {
  LuDollarSign,
  LuTrash2,
  LuArrowLeft,
  LuHistory,
  LuLoader,
  LuSave,
  LuWallet,
  LuPencil,
  LuX
} from "react-icons/lu";
import { useNotification } from "../../common/NotificationContext";
import { InputField } from "@cakrawalajakarta/shared-components";

// PayPal money lives in a different account than Midtrans, so its balance and
// withdrawals are tracked separately from Finance & Balance. Amounts are shown
// in IDR to match the dashboard, which records PayPal revenue at the category
// price converted when the order was created.
export const FinancePaypal = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    netRevenue: 0,
    totalWithdrawals: 0,
    balance: 0,
    transactionCount: 0,
    grossUSD: 0,
    feeUSD: 0,
    netUSD: 0,
    unverifiedFeeCount: 0
  });
  const [withdrawals, setWithdrawals] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    amount: "",
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5)
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const participantsRef = collection(db, "pendaftaranJOTR2026");
      const participantsSnapshot = await getDocs(participantsRef);

      let net = 0;
      let count = 0;
      let grossUSD = 0;
      let feeUSD = 0;
      let netUSD = 0;
      let unverifiedFeeCount = 0;

      participantsSnapshot.forEach((docSnap) => {
        const p = docSnap.data();
        const isSettled = p.transaction_status === "settlement" || p.transaction_status === "capture";
        const isPayPalCompleted = p.paypalCapture?.status === "COMPLETED";
        const isPayPalType = p.paymentMethod === "paypal" || p.payment_type === "paypal";
        if (!(isSettled || isPayPalCompleted)) return;

        const isPayPal = isPayPalCompleted || (isPayPalType && isSettled) || p.currency === "USD";
        if (!isPayPal) return;

        const isFree =
          p.payment_type === "free" ||
          p.payment_type === "free_voucher" ||
          Number(p.gross_amount) === 0 ||
          Number(p.harga) === 0 ||
          !p.harga;
        if (isFree) return;

        // paypalAmountIDR is the IDR price after voucher that the USD charge was
        // converted from (markup covers PayPal fees and the withdrawal spread).
        // Older records without it fall back to the category price.
        const amountIDR = p.paypalAmountIDR != null ? Number(p.paypalAmountIDR) : Number(p.harga) || 0;
        if (amountIDR <= 0) return;

        net += amountIDR;
        count += 1;

        const gross = Number(p.paypalGrossUSD ?? p.gross_amount) || 0;
        grossUSD += gross;
        if (p.paypalNetUSD != null) {
          feeUSD += Number(p.paypalFeeUSD) || 0;
          netUSD += Number(p.paypalNetUSD);
        } else {
          unverifiedFeeCount += 1;
        }
      });

      const wQuery = query(
        collection(db, "withdrawals"),
        where("eventId", "==", "JOTR2026"),
        where("channel", "==", "paypal"),
        orderBy("date", "desc")
      );
      const wSnapshot = await getDocs(wQuery);
      const wList = wSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const totalW = wList.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

      setWithdrawals(wList);
      setStats({
        netRevenue: net,
        totalWithdrawals: totalW,
        balance: net - totalW,
        transactionCount: count,
        grossUSD,
        feeUSD,
        netUSD,
        unverifiedFeeCount
      });
    } catch (error) {
      console.error("Error fetching PayPal finance data:", error);
      showNotification("error", "Failed to load PayPal finance data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || Number(formData.amount) <= 0) {
      showNotification("error", "Please enter a valid amount");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = {
        amount: Number(formData.amount),
        date: formData.date,
        time: formData.time,
        eventId: "JOTR2026",
        channel: "paypal",
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        await updateDoc(doc(db, "withdrawals", editingId), dataToSave);
        showNotification("success", "PayPal withdrawal updated successfully");
      } else {
        await addDoc(collection(db, "withdrawals"), {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
        showNotification("success", "PayPal withdrawal added successfully");
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving PayPal withdrawal:", error);
      showNotification("error", "Failed to save PayPal withdrawal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (w) => {
    setIsEditing(true);
    setEditingId(w.id);
    setFormData({
      amount: w.amount.toString(),
      date: w.date,
      time: w.time || "00:00"
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      amount: "",
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5)
    });
  };

  const handleDeleteWithdrawal = async (id) => {
    if (!confirm("Are you sure you want to delete this PayPal withdrawal entry?")) return;

    try {
      await deleteDoc(doc(db, "withdrawals", id));
      showNotification("success", "PayPal withdrawal deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting PayPal withdrawal:", error);
      showNotification("error", "Failed to delete PayPal withdrawal");
    }
  };

  const formatIDR = (val) => `Rp ${Math.round(Number(val) || 0).toLocaleString('en-US')}`;
  const formatUSD = (val) => `$${(Number(val) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading && !stats.netRevenue) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-slate-600"
          >
            <LuArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">PayPal Finance &amp; Balance</h1>
            <p className="text-sm text-slate-500">Balance and withdrawals in IDR; PayPal settlement in USD</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Total Balance</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.balance)}</p>
            </div>
            <LuWallet size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Net Revenue</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.netRevenue)}</p>
              <p className="text-sm text-slate-400 mt-1">Price after voucher (markup covers PayPal fees)</p>
            </div>
            <LuDollarSign size={24} className="text-slate-300" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-sm font-medium mb-1">Total Withdrawals</p>
              <p className="text-xl font-bold text-slate-800">{formatIDR(stats.totalWithdrawals)}</p>
            </div>
            <LuHistory size={24} className="text-slate-300" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">PayPal Transactions</p>
          <p className="text-xl font-bold text-slate-800">{stats.transactionCount}</p>
          <p className="text-sm text-slate-400 mt-1">Paid transactions (incl. transferred bibs)</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Gross (USD)</p>
          <p className="text-xl font-bold text-slate-800">{formatUSD(stats.grossUSD)}</p>
          <p className="text-sm text-slate-400 mt-1">Charged to buyers</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">PayPal Fee (USD)</p>
          <p className="text-xl font-bold text-slate-800">{formatUSD(stats.feeUSD)}</p>
          <p className="text-sm text-slate-400 mt-1">Actual fee reported by PayPal</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <p className="text-slate-500 text-sm font-medium mb-1">Net Received (USD)</p>
          <p className="text-xl font-bold text-slate-800">{formatUSD(stats.netUSD)}</p>
          <p className="text-sm text-slate-400 mt-1">
            {stats.unverifiedFeeCount > 0
              ? `${stats.unverifiedFeeCount} transaction(s) without PayPal fee data`
              : "Verified against PayPal"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Withdrawal Form */}
        <div className="lg:col-span-1 space-y-4">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-2">
              <h3 className="font-bold text-slate-800">{isEditing ? "Edit Withdrawal" : "Record Withdrawal"}</h3>
              {isEditing && (
                <button type="button" onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                  <LuX size={18} />
                </button>
              )}
            </div>

            <div className="space-y-4">
              <InputField
                label="Amount (IDR)"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="e.g. 5000000"
                required
                labelColor="text-slate-700"
              />

              <div className="grid grid-cols-2 gap-2">
                <InputField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  labelColor="text-slate-700"
                />
                <InputField
                  label="Time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  labelColor="text-slate-700"
                />
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className={`w-full py-2 ${isEditing ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-800 hover:bg-slate-900"} text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2`}
              >
                {isSaving ? <LuLoader className="animate-spin" size={16} /> : <LuSave size={16} />}
                {isSaving ? "Saving..." : (isEditing ? "Update Withdrawal" : "Save Withdrawal")}
              </button>
            </div>
          </form>
        </div>

        {/* Withdrawal History List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden h-full">
            <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800">Withdrawal History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-slate-500">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Date &amp; Time</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="p-8 text-center text-slate-400 italic">
                        No withdrawal history found.
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((w) => {
                      const dateTimeStr = w.date
                        ? new Date(`${w.date}T${w.time || "00:00"}`)
                            .toLocaleString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })
                            .replace(',', '')
                        : "—";

                      return (
                        <tr
                          key={w.id}
                          className={`hover:bg-slate-50 ${editingId === w.id ? "bg-blue-50" : ""}`}
                        >
                          <td className="px-4 py-3 font-medium text-slate-800">
                            {dateTimeStr}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-slate-800">
                            {formatIDR(w.amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleEdit(w)}
                                className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                                title="Edit"
                              >
                                <LuPencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteWithdrawal(w.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <LuTrash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
